import React from 'react';

/* Small presentational helpers shared across the customer shell's pages
   (Home / Songs / More / Family / Announcements). They lean entirely on the
   portal theme's CSS custom properties (set on .portal-shell), so every surface
   follows Default / Comfort / Comfort-Dark with the chrome. Mobile-first: the
   page column is a single centered stack, capped for the wider desktop width. */

export const PageWrap = ({ children, style }) => (
	<div
		style={{
			maxWidth: 760,
			margin: '0 auto',
			padding: '1.25rem 1rem 1.5rem',
			...style,
		}}>
		{children}
	</div>
);

// A serif page title + optional subtitle (matches the mockup's screen headers).
export const PageHead = ({ title, subtitle }) => (
	<div style={{ marginBottom: '1rem' }}>
		<h1
			style={{
				fontFamily: 'var(--font-serif)',
				fontWeight: 'var(--fw-semibold, 600)',
				fontSize: '1.6rem',
				color: 'var(--text-heading, #384971)',
				margin: 0,
				lineHeight: 1.1,
			}}>
			{title}
		</h1>
		{subtitle && (
			<p
				style={{
					margin: '0.35rem 0 0',
					color: 'var(--text-muted, #7b8499)',
					fontSize: '0.9rem',
					lineHeight: 1.5,
				}}>
				{subtitle}
			</p>
		)}
	</div>
);

export const Card = ({ children, style }) => (
	<div
		style={{
			background: 'var(--surface-card, #fff)',
			border: '1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
			borderRadius: 'var(--radius-lg, 16px)',
			boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(56,73,113,.05))',
			overflow: 'hidden',
			...style,
		}}>
		{children}
	</div>
);

// Uppercase section eyebrow with a leading icon + optional right-aligned action.
export const SectionHead = ({ icon, label, color, action, onAction, to }) => {
	const ActionTag = to ? 'a' : 'button';
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				padding: '12px 15px 10px',
			}}>
			{icon && (
				<i
					className={`fa-sharp fa-solid ${icon}`}
					style={{ fontSize: 12, color: color || 'var(--cyv-teal-600, #2f7d77)' }}
					aria-hidden='true'
				/>
			)}
			<span
				style={{
					fontSize: '0.68rem',
					fontWeight: 'var(--fw-bold, 700)',
					letterSpacing: '0.12em',
					textTransform: 'uppercase',
					color: 'var(--text-muted, #a6adba)',
				}}>
				{label}
			</span>
			{action && (
				<ActionTag
					href={to || undefined}
					onClick={onAction}
					style={{
						marginLeft: 'auto',
						background: 'none',
						border: 'none',
						padding: 0,
						cursor: 'pointer',
						fontSize: '0.78rem',
						fontWeight: 'var(--fw-bold, 700)',
						color: 'var(--cyv-teal-600, #2f7d77)',
						textDecoration: 'none',
						fontFamily: 'inherit',
					}}>
					{action}
				</ActionTag>
			)}
		</div>
	);
};

// A calm, forward-pointing empty block (never an error, never blank).
export const QuietState = ({ icon, children }) => (
	<div
		style={{
			padding: '6px 15px 18px',
			display: 'flex',
			alignItems: 'center',
			gap: 11,
		}}>
		<span
			style={{
				width: 34,
				height: 34,
				borderRadius: '50%',
				background: 'var(--psp-chip-track, rgba(0,0,0,.05))',
				color: 'var(--text-muted, #9aa2b1)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flex: '0 0 auto',
			}}>
			<i className={`fa-sharp fa-solid ${icon}`} style={{ fontSize: 13 }} aria-hidden='true' />
		</span>
		<span style={{ fontSize: '0.85rem', color: 'var(--text-muted, #7b8499)', lineHeight: 1.5 }}>
			{children}
		</span>
	</div>
);

// Display name for a student doc (mirrors MyLessons' resolution order).
export const studentDisplayName = (s) =>
	s.fullName ||
	[s.firstName, s.lastName].filter(Boolean).join(' ') ||
	s.name ||
	s.studentName ||
	'Student';
